/**
 * C.11 — NIPE chat hook.
 *
 * Owns:
 *   - in-memory message history for the active chat
 *   - streaming buffer + abort controller
 *   - route decision (BYOK direct vs Convex backup)
 *   - tool-call loop (cap 4 rounds)
 *   - audit logging via api.nipeAuditLog.logEvent
 *   - persisted messages via api.nipeMessages.appendMessage
 *
 * The hook is dumb about UI — it returns `messages`, `streaming`,
 * `route`, `send()`, `cancel()` and exposes the most recent assistant
 * tail for the bubble to render.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAction, useConvex, useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { streamCompletion } from '../../lib/openrouter/client';
import { chooseRoute, maskKey, type NipeRoute } from '../../lib/nipe/chooseRoute';
import { applyToneConstraints, BASE_SYSTEM_PROMPT } from '../../lib/nipe/toneSystemPrompt';
import {
  buildWindowContextBlock,
  type WindowSnapshot,
} from '../../lib/nipe/windowContext';
import { SKILL_TOOLS, serializeTools } from '../../lib/nipe/toolSchemas';
import { runToolCall, type ToolRunnerContext, type ToolCallShape } from '../../lib/nipe/toolRunner';
import { loadSampleVault } from '../../lib/vault/sampleVault';
import { listChildren } from '../../lib/graph/sharepoint';
import { usePortalDrives, usePortalSites } from '../../lib/tenant/usePortalSharePoint';
import {
  bodyPreview,
  hashBody,
  setAutoApproveAttribution,
  setPendingWriteBody,
} from '../../lib/nipe/pendingWriteBodies';
import { evaluateAutoApprove } from '../../lib/graph/autoApprove';
import { useWindowContext } from '../desktop/WindowContext';
import { setLabHandoff } from '../specialization-lab/labHandoff';

const OPENROUTER_KEY_STORAGE = 'repancy-openrouter-key';
const DEFAULT_MODEL = 'anthropic/claude-sonnet-4.6';
const MAX_TOOL_ROUNDS = 4;

export type ChatRole = 'user' | 'assistant' | 'tool' | 'system';

export interface NipeChatMessage {
  id: string;
  role: ChatRole;
  content: string;
  toolCallsJson?: string;
  toolCallId?: string;
  streaming?: boolean;
}

export interface UseNipeChatOptions {
  clientSlug: string;
  windows: ReadonlyArray<WindowSnapshot>;
  focusedNotePath?: string | null;
}

export interface UseNipeChatResult {
  messages: NipeChatMessage[];
  streamingText: string;
  route: NipeRoute;
  isRunning: boolean;
  error: string | null;
  hasKey: boolean;
  /** Masked OpenRouter key (empty when no BYOK key is set). */
  maskedKey: string;
  send: (userText: string) => Promise<void>;
  cancel: () => void;
  reset: () => void;
}

function readOpenRouterKey(): string {
  if (typeof window === 'undefined') return '';
  try {
    return localStorage.getItem(OPENROUTER_KEY_STORAGE)?.trim() ?? '';
  } catch {
    return '';
  }
}

function makeId(): string {
  return `m-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
}

export function useNipeChat(opts: UseNipeChatOptions): UseNipeChatResult {
  const [messages, setMessages] = useState<NipeChatMessage[]>([]);
  const [streamingText, setStreamingText] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [keyTick, setKeyTick] = useState(0);

  const abortRef = useRef<AbortController | null>(null);

  const openRouterKey = useMemo(readOpenRouterKey, [keyTick]);
  const route: NipeRoute = useMemo(
    () => chooseRoute({ demoMode: false, userOpenRouterKey: openRouterKey }),
    [openRouterKey],
  );

  // Storage event so the route badge updates when the user pastes a key
  // in Settings without reloading.
  useEffect(() => {
    const onStorage = (e: StorageEvent): void => {
      if (e.key === OPENROUTER_KEY_STORAGE) setKeyTick((t) => t + 1);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const logEvent = useMutation(api.nipeAuditLog.logEvent);
  const appendMessage = useMutation(api.nipeMessages.appendMessage);
  // C.11b — get-or-create the single nipeChats row for this
  // (signedInUser, portal). Cached in a ref so we only hit the network
  // on the very first send per hook mount.
  const getOrCreateChat = useMutation(api.nipeChats.getOrCreateForPortal);
  const chatIdRef = useRef<string | null>(null);

  // C.11b — drop the cached chatId when the portal changes so the next
  // send hits getOrCreateForPortal for the new slug instead of writing
  // into the previous portal's thread.
  useEffect(() => {
    chatIdRef.current = null;
  }, [opts.clientSlug]);

  const convexChat = useAction(api.nipeChat.chat);
  const convex = useConvex();
  // L.15.5 — expose `openSpecializationLab` callback so NIPE can
  // navigate the user to the Lab. Read-only; NIPE never twists Lab knobs.
  // F-NIPE-DESKTOP-AWARENESS — also pulls closeWindow/focusWindow/
  // minimizeWindow + the `windows` snapshot for the listOpenWindows tool.
  const {
    openWindow,
    closeWindow,
    focusWindow,
    minimizeWindow,
    windows,
  } = useWindowContext();
  // Stable snapshot getter — the tool needs the freshest array at call
  // time, not the closure-captured one from this render.
  const windowsRef = useRef(windows);
  windowsRef.current = windows;

  // Skills catalog for the portal — paired demo + live (name + description).
  const skillsList = useQuery(
    api.skills.listForSlug,
    opts.clientSlug ? { clientSlug: opts.clientSlug } : 'skip',
  );

  // SP audit logger — paired demo per `feedback_paired_audit_tables`.
  const logSpEvent = useMutation(api.nipeSharePointAuditLog.logEvent);

  // F3 — propose-write mutation; branches live/demo inside the function.
  const proposeWrite = useMutation(api.nipePendingWrites.propose);
  // B.4 — auto-approve decision mutation. Same `decide` used by the tray
  // when a human approves; calling it directly after `propose` shortcuts
  // the tray for matched rules.
  const decideWrite = useMutation(api.nipePendingWrites.decide);
  // B.4 — enabled rules for the portal. Empty list (or query loading)
  // ⇒ no auto-approve, falls through to manual tray flow.
  const approvalRules = useQuery(
    api.nipeApprovalRules.listEnabled,
    opts.clientSlug
      ? {
          clientSlug: opts.clientSlug,
        }
      : 'skip',
  );

  // F6 — live vault for the portal (paired demo). When present, NIPE's
  // vault tools read from these rows instead of the committed sample
  // vault.
  const liveVault = useQuery(
    api.vaults.getBySlug,
    opts.clientSlug
      ? {
          clientSlug: opts.clientSlug,
        }
      : 'skip',
  );
  const liveNotes = useQuery(
    api.vaults.listNotesByVault,
    liveVault?._id
      ? {
          vaultId: String(liveVault._id),
        }
      : 'skip',
  );

  // Resolve the portal's first site + its first drive so NIPE can
  // call listChildren without the user having to specify a path.
  // usePortalSites / usePortalDrives are no-ops in demo mode and
  // when MSAL is not authenticated.
  const sitesState = usePortalSites();
  const firstSiteId =
    sitesState.kind === 'ok' && sitesState.value.length > 0
      ? (sitesState.value[0]!.id ?? null)
      : null;
  const drivesState = usePortalDrives(firstSiteId);
  const firstDriveId =
    drivesState.kind === 'ok' && drivesState.value.length > 0 ? drivesState.value[0]!.id : null;

  // Tool runner context. Built once per render; the runner functions
  // close over the latest Convex + sample-vault data via the
  // surrounding hook. listSkills + getSkill fall back to the
  // committed sample vault's Skills/ folder when the Convex skills
  // table is empty (demo bootstrap case).
  const toolContext = useMemo<ToolRunnerContext>(() => {
    const sample = loadSampleVault();
    const sampleSkillNotes = sample.notes.filter((n) => n.path.startsWith('Skills/') && n.path.endsWith('SKILL.md'));
    const sampleSkills = sampleSkillNotes.map((n) => {
      // Path looks like "Skills/redline-differ/SKILL.md" — use the
      // folder name as the Skill name.
      const parts = n.path.split('/');
      const name = parts.length >= 2 ? parts[1]! : n.path;
      const body = n.body ?? '';
      const descMatch = /\ndescription:\s*([^\n]+)/i.exec(body);
      const description = descMatch ? descMatch[1]!.trim() : '';
      return { name, description, body };
    });

    return {
      listSkills: async () => {
        const live = (skillsList ?? []).map((r) => ({
          name: r.name,
          description: r.description ?? '',
          body: '', // body lives in skillVersions; listSkills only needs name+description
        }));
        // Merge live + sample, preferring live by name.
        const liveNames = new Set(live.map((s) => s.name));
        return [...live, ...sampleSkills.filter((s) => !liveNames.has(s.name))];
      },
      getSkill: async (name) => {
        // F1 — fetch the latest skillVersions body from Convex when the
        // Skill row exists for this portal. Falls back to the committed
        // sample vault's Skills/ folder when the portal has no such skill.
        try {
          if (opts.clientSlug) {
            const row = await convex.query(api.skills.getBodyByName, {
              clientSlug: opts.clientSlug,
              name,
            });
            if (row) return { name: row.name, body: row.body };
          }
        } catch {
          // Convex unreachable → fall through to sample.
        }
        const sampleHit = sampleSkills.find((s) => s.name === name);
        if (sampleHit) return { name: sampleHit.name, body: sampleHit.body };
        return null;
      },
      searchVault: async (query, limit) => {
        // F6 — prefer live vault rows (paired demo) when the portal has
        // a vault; otherwise fall through to the committed sample.
        const corpus = liveNotes && liveNotes.length > 0 ? liveNotes : sample.notes;
        const q = query.toLowerCase();
        const hits = corpus
          .map((n) => {
            const body = n.body ?? '';
            const idx = body.toLowerCase().indexOf(q);
            const score = idx >= 0 ? 1 : n.path.toLowerCase().includes(q) ? 0.5 : 0;
            if (score === 0) return null;
            const snippet =
              idx >= 0
                ? body.slice(Math.max(0, idx - 40), idx + 120)
                : body.slice(0, 120) || n.path;
            return { path: n.path, snippet, score };
          })
          .filter((x): x is { path: string; snippet: string; score: number } => x !== null)
          .sort((a, b) => b.score - a.score)
          .slice(0, limit);
        return hits;
      },
      openVaultNote: async (path) => {
        // F6 — prefer live row; fall through to sample.
        const live = (liveNotes ?? []).find((n) => n.path === path);
        if (live) return { path: live.path, body: live.body ?? '' };
        const note = sample.notes.find((n) => n.path === path);
        if (!note) return null;
        return { path: note.path, body: note.body ?? '' };
      },
      proposeSharePointDocument: async ({ folderPath, fileName, body, summary }) => {
        const hash = await hashBody(body);
        const preview = bodyPreview(body);
        const pendingWriteId = await proposeWrite({
          clientSlug: opts.clientSlug,
          kind: 'sp-create-doc',
          folderPath,
          fileName,
          bodyHash: hash,
          bodyPreview: preview,
          summary,
        });
        setPendingWriteBody(pendingWriteId, body);
        // B.4 — auto-approve hook. If an enabled rule matches the
        // folder path, immediately call decide({approved}) so the tray
        // drain worker picks it up without user interaction. Manual
        // approve path stays intact: rules with no match (or empty
        // rule list) fall through to the existing tray flow.
        const autoApprove = evaluateAutoApprove(
          { folderPath },
          approvalRules ?? [],
        );
        if (autoApprove.shouldAutoApprove) {
          // B.6 — Record the rule attribution so the tray's approval
          // handler can pass it to the SP audit log entry.
          if (autoApprove.matchedRuleId && autoApprove.matchedRuleLabel) {
            setAutoApproveAttribution(pendingWriteId, {
              ruleId: autoApprove.matchedRuleId,
              ruleLabel: autoApprove.matchedRuleLabel,
            });
          }
          await decideWrite({
            pendingWriteId,
            decision: 'approved',
          });
        }
        return { pendingWriteId, status: 'queued' as const };
      },
      listSharePointDocs: firstDriveId
        ? async (matterPath) => {
            const argsJson = JSON.stringify({ matterPath: matterPath ?? null, driveId: firstDriveId });
            try {
              const children = await listChildren(firstDriveId, 'root');
              void logSpEvent({
                clientSlug: opts.clientSlug,
                method: 'listChildren',
                args: argsJson,
                outcome: 'ok',
                detail: `count=${children.length}`,
              }).catch(() => undefined);
              return children
                .filter((c) => 'file' in c || 'folder' in c)
                .map((c) => ({
                  name: c.name ?? '(unnamed)',
                  webUrl: c.webUrl ?? undefined,
                  id: c.id ?? undefined,
                }));
            } catch (err) {
              const msg = err instanceof Error ? err.message : String(err);
              void logSpEvent({
                clientSlug: opts.clientSlug,
                method: 'listChildren',
                args: argsJson,
                outcome: 'error',
                detail: msg.slice(0, 200),
              }).catch(() => undefined);
              throw err;
            }
          }
        : undefined,
      // F18 — Customer onboarding wizard tools (5).
      // Bridge to `window.__repancyOnboardWizard` registered by
      // `src/routes/onboard/new.tsx`. When the wizard isn't mounted, the
      // tools' optional-callback branch returns a friendly error message.
      openOnboardWizard: async (step) => {
        const bridge = window.__repancyOnboardWizard;
        if (!bridge) {
          // Wizard not mounted — return a navigation hint. The actual
          // navigation is done by NIPE rendering a link the user clicks.
          return { opened: false, currentStep: 'intro' as const };
        }
        if (step) await bridge.goToStep(step);
        // The bridge's OnboardStep is wider ('completed' included); the
        // tool's OnboardWizardStep excludes 'completed' (completion is
        // a terminal state, not a target). Narrow to the tool's union.
        const cur = bridge.currentStep();
        const reportable = cur === 'completed' ? ('provisioning' as const) : cur;
        return { opened: true, currentStep: reportable };
      },
      setFormField: async (name, value) => {
        const bridge = window.__repancyOnboardWizard;
        if (!bridge) return { ok: false, previousValue: null };
        return await bridge.setField(name, value);
      },
      inspectTenant: async (args) => {
        if (args.kind === 'domain') {
          const { probeTenantDomain } = await import('../../lib/onboard/tenantDiscovery');
          const result = await probeTenantDomain(args.domain);
          if (result.kind === 'ok') {
            return {
              kind: 'ok' as const,
              tenantId: result.tenantId,
              issuer: result.issuer,
              region: result.region,
            };
          }
          return {
            kind: 'error' as const,
            reason: result.reason,
            message: result.message,
          };
        }
        // F18.next — real `kind='app-reg'` probe via Convex action.
        // The action mints an app-only token in the customer's tenant
        // via client-credentials, then probes /v1.0/organization.
        try {
          const result = await convex.action(
            api.onboardingTenantProbe.inspectTenantAppReg,
            { tenantId: args.tenantId },
          );
          if (result.kind === 'ok') {
            return {
              kind: 'ok' as const,
              consented: result.consented,
              missingScopes: result.missingScopes,
              tenantDisplayName: result.displayName,
            };
          }
          return {
            kind: 'error' as const,
            reason: 'probe-failed',
            message: result.message,
          };
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          return {
            kind: 'error' as const,
            reason: 'probe-failed',
            message: msg,
          };
        }
      },
      kickAdminConsent: async (args) => {
        // Round 1 hard limit: NEVER programmatic redirect. We build the
        // URL and return it — the wizard route renders the <a href> for
        // the user to click.
        const clientId =
          (import.meta as unknown as { env?: { VITE_MS_APP_CLIENT_ID?: string } })
            .env?.VITE_MS_APP_CLIENT_ID ?? '';
        const params = new URLSearchParams({
          client_id: clientId,
          redirect_uri:
            typeof window !== 'undefined' ? window.location.origin : '',
          state: 'onboard-wizard',
        });
        const consentUrl = `https://login.microsoftonline.com/${args.tenantId}/adminconsent?${params.toString()}`;
        // Stash the wizard's return intent so the landing surface can
        // resume after the Microsoft round-trip.
        let intentStashed = false;
        try {
          const { ADMIN_CONSENT_INTENT_KEY } = await import(
            '../../lib/onboard/adminConsentLanding'
          );
          window.sessionStorage.setItem(
            ADMIN_CONSENT_INTENT_KEY,
            JSON.stringify({
              origin: 'onboard-wizard-nipe',
              returnTo: args.returnPath,
              startedAt: new Date().toISOString(),
            }),
          );
          intentStashed = true;
        } catch {
          // SSR / private mode; non-fatal.
        }
        return { consentUrl, intentStashed };
      },
      submitWizardStep: async () => {
        const bridge = window.__repancyOnboardWizard;
        if (!bridge) {
          return {
            kind: 'error' as const,
            message: 'Wizard not mounted',
          };
        }
        return await bridge.submitCurrentStep();
      },
      // L.15.5 — Specialization Lab navigation. Opens the registered
      // window via WindowContext; the read-only bridge for control
      // introspection is owned by SpecializationLabWindow itself.
      // F18.G.3 — when NIPE supplies skillName/probeText, write a Lab
      // handoff record so the Lab seeds itself on mount.
      openSpecializationLab: async (labArgs) => {
        try {
          if (
            (labArgs && typeof labArgs.skillName === 'string') ||
            (labArgs && typeof labArgs.probeText === 'string')
          ) {
            setLabHandoff({
              skillName: labArgs.skillName,
              probeText: labArgs.probeText,
              source: 'nipe.openSpecializationLab',
            });
          }
          openWindow('/specialization-lab');
          return { opened: true };
        } catch {
          return { opened: false };
        }
      },
      // Generic openWindow — lets NIPE drive any catalogued Repancy
      // window (DocumentLibrary, PromptLab, SkillLibrary, etc.). The
      // bespoke per-window openers above stay for backward-compat;
      // the model may call either.
      openWindow: async (path: string) => {
        try {
          openWindow(path);
          return { opened: true, title: path };
        } catch {
          return { opened: false, title: path };
        }
      },
      // F-NIPE-DESKTOP-AWARENESS — siblings to openWindow. Each
      // wraps the matching WindowContext method; all local UX state,
      // no remote mutation, never gated.
      closeWindow: async (path: string) => {
        try {
          closeWindow(path);
          return { closed: true };
        } catch {
          return { closed: false };
        }
      },
      focusWindow: async (path: string) => {
        try {
          focusWindow(path);
          return { focused: true };
        } catch {
          return { focused: false };
        }
      },
      minimizeWindow: async (path: string) => {
        try {
          minimizeWindow(path);
          return { minimized: true };
        } catch {
          return { minimized: false };
        }
      },
      // listOpenWindows returns a snapshot synchronously. windowsRef
      // is kept up-to-date on every render so the tool sees fresh
      // state without re-memoizing the entire ctx on each window
      // change.
      listOpenWindows: () =>
        windowsRef.current.map((w) => ({
          path: w.path,
          title: w.title,
          state: w.state,
          zIndex: w.zIndex,
        })),
      // F-NIPE-DESKTOP-AWARENESS — per-window selection bridges.
      // Each registered window exposes a `window.__repancy*` getter;
      // we dispatch by path and call the getter. typeof check so SSR
      // / test envs without a real window return null safely.
      // F-NIPE-PROMPT-LAB-DRIVER — bespoke Prompt Lab tools. Driver
      // bridge is registered by PromptLabWindow on mount; when the
      // window isn't mounted, every callback surfaces the friendly
      // "Prompt Lab is not mounted" error in the tool result.
      readPromptSession: () => {
        if (typeof window === 'undefined') return null;
        return window.__repancyPromptLabDriver?.readActiveSession() ?? null;
      },
      getActivePromptSessionId: () => {
        if (typeof window === 'undefined') return null;
        return window.__repancyPromptLabDriver?.getActiveSessionId() ?? null;
      },
      setPromptSessionSystemPrompt: (sessionId: string, text: string) => {
        if (typeof window === 'undefined') {
          return { ok: false, reason: 'no window' };
        }
        return (
          window.__repancyPromptLabDriver?.setSystemPrompt(sessionId, text) ?? {
            ok: false,
            reason: 'Prompt Lab is not mounted in this session.',
          }
        );
      },
      setPromptSessionUserPrompt: (sessionId: string, text: string) => {
        if (typeof window === 'undefined') {
          return { ok: false, reason: 'no window' };
        }
        return (
          window.__repancyPromptLabDriver?.setUserPrompt(sessionId, text) ?? {
            ok: false,
            reason: 'Prompt Lab is not mounted in this session.',
          }
        );
      },
      // F-NIPE-PROMPT-LAB-SKILLS — skill-prompt CRUD wrappers. Each
      // surfaces the same "Prompt Lab is not mounted" fallback so the
      // tool result is always a structured { ok, reason }.
      addLibrarySkillPromptToSession: (
        sessionId: string,
        skillName: string,
        weight: number,
        enabled: boolean,
      ) => {
        if (typeof window === 'undefined') {
          return { ok: false, reason: 'no window' };
        }
        return (
          window.__repancyPromptLabDriver?.addLibrarySkill(
            sessionId,
            skillName,
            weight,
            enabled,
          ) ?? {
            ok: false,
            reason: 'Prompt Lab is not mounted in this session.',
          }
        );
      },
      addCustomSkillPromptToSession: (
        sessionId: string,
        name: string,
        content: string,
        weight: number,
        enabled: boolean,
      ) => {
        if (typeof window === 'undefined') {
          return { ok: false, reason: 'no window' };
        }
        return (
          window.__repancyPromptLabDriver?.addCustomSkill(
            sessionId,
            name,
            content,
            weight,
            enabled,
          ) ?? {
            ok: false,
            reason: 'Prompt Lab is not mounted in this session.',
          }
        );
      },
      removeSkillPromptFromSession: (sessionId: string, skillPromptId: string) => {
        if (typeof window === 'undefined') {
          return { ok: false, reason: 'no window' };
        }
        return (
          window.__repancyPromptLabDriver?.removeSkill(
            sessionId,
            skillPromptId,
          ) ?? {
            ok: false,
            reason: 'Prompt Lab is not mounted in this session.',
          }
        );
      },
      updateSkillPromptOnSession: (
        sessionId: string,
        skillPromptId: string,
        changes: {
          name?: string;
          content?: string;
          weight?: number;
          enabled?: boolean;
        },
      ) => {
        if (typeof window === 'undefined') {
          return { ok: false, reason: 'no window' };
        }
        return (
          window.__repancyPromptLabDriver?.updateSkill(
            sessionId,
            skillPromptId,
            changes,
          ) ?? {
            ok: false,
            reason: 'Prompt Lab is not mounted in this session.',
          }
        );
      },
      readWindowContext: (path: string) => {
        if (typeof window === 'undefined') return null;
        switch (path) {
          case '/obsidian-vault':
            return window.__repancyObsidianVault?.() ?? null;
          case '/documents':
            return window.__repancyDocumentLibrary?.() ?? null;
          case '/settings':
            return window.__repancySettings?.() ?? null;
          case '/skills':
            return window.__repancySkillLibrary?.() ?? null;
          case '/prompt-lab':
            return window.__repancyPromptLab?.() ?? null;
          case '/corpus-search':
            return window.__repancyCorpusSearch?.() ?? null;
          case '/clients':
            return window.__repancyClientList?.() ?? null;
          case '/obsidian-import':
            return window.__repancyObsidianImport?.() ?? null;
          default:
            return null;
        }
      },
    };
  }, [skillsList, firstDriveId, logSpEvent, proposeWrite, decideWrite, approvalRules, liveNotes, opts.clientSlug, convex, openWindow, closeWindow, focusWindow, minimizeWindow]);

  const cancel = useCallback((): void => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsRunning(false);
    void logEvent({
      clientSlug: opts.clientSlug,
      event: 'cancel',
    }).catch(() => undefined);
  }, [opts.clientSlug, logEvent]);

  const reset = useCallback((): void => {
    setMessages([]);
    setStreamingText('');
    setError(null);
    setIsRunning(false);
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const send = useCallback(
    async (userText: string): Promise<void> => {
      const trimmed = userText.trim();
      if (trimmed.length === 0) return;
      if (isRunning) return;

      const windowBlock = buildWindowContextBlock({
        windows: opts.windows,
        portalSlug: opts.clientSlug || null,
        isDemo: false,
        focusedNotePath: opts.focusedNotePath ?? null,
      });

      const userMsg: NipeChatMessage = {
        id: makeId(),
        role: 'user',
        content: trimmed,
      };
      setMessages((prev) => [...prev, userMsg]);
      setError(null);
      setIsRunning(true);
      setStreamingText('');

      void logEvent({
        clientSlug: opts.clientSlug,
        event: 'send',
        detail: trimmed.slice(0, 200),
      }).catch(() => undefined);

      // C.11b — persist user message (best-effort; UI does not wait on
      // it). On the first send of this hook mount we lazily get-or-
      // create the (user, portal) chat row; subsequent sends reuse the
      // cached id. The fire-and-forget pattern means even if the
      // mutation throws we don't block the model call.
      void (async (): Promise<void> => {
        try {
          if (chatIdRef.current === null) {
            const id = await getOrCreateChat({
              clientSlug: opts.clientSlug,
              modelId: DEFAULT_MODEL,
              route,
            });
            chatIdRef.current = String(id);
          }
          await appendMessage({
            chatId: chatIdRef.current,
            clientSlug: opts.clientSlug,
            role: 'user',
            content: trimmed,
          });
        } catch {
          // Persistence failure is non-fatal; chat UI keeps working.
        }
      })();

      try {
        if (route === 'byok-direct') {
          await runByokDirect(trimmed, windowBlock);
        } else {
          await runConvexBackup(trimmed, windowBlock);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setIsRunning(false);
        setStreamingText('');
      }
    },
    [
      isRunning,
      opts.windows,
      opts.clientSlug,
      opts.focusedNotePath,
      route,
      logEvent,
      appendMessage,
      getOrCreateChat,
    ],
  );

  const runByokDirect = useCallback(
    async (userText: string, windowBlock: string): Promise<void> => {
      const controller = new AbortController();
      abortRef.current = controller;
      const composedUserContent = `${windowBlock}\n\n${userText}`;
      const apiMessages: Array<{ role: string; content: string }> = [
        { role: 'system', content: BASE_SYSTEM_PROMPT },
        ...messages.map((m) => ({ role: m.role, content: m.content })),
        { role: 'user', content: composedUserContent },
      ];
      let accumulated = '';
      await streamCompletion({
        model: DEFAULT_MODEL,
        messages: apiMessages,
        apiKey: openRouterKey,
        signal: controller.signal,
        onChunk: (chunk) => {
          accumulated += chunk;
          setStreamingText(accumulated);
        },
        onComplete: (fullText) => {
          const cleaned = applyToneConstraints(fullText).cleaned;
          setMessages((prev) => [
            ...prev,
            { id: makeId(), role: 'assistant', content: cleaned },
          ]);
          void logEvent({
            clientSlug: opts.clientSlug,
            event: 'stream',
            detail: 'byok-direct complete',
          }).catch(() => undefined);
          abortRef.current = null;
        },
        onError: (err) => {
          setError(err.message);
          abortRef.current = null;
        },
      });
    },
    [messages, openRouterKey, opts.clientSlug, logEvent],
  );

  const runConvexBackup = useCallback(
    async (userText: string, windowBlock: string): Promise<void> => {
      const composedUserContent = `${windowBlock}\n\n${userText}`;
      type ApiMsg = {
        role: 'system' | 'user' | 'assistant' | 'tool';
        content: string;
        toolCallsJson?: string;
        toolCallId?: string;
      };
      const baseMessages: ApiMsg[] = [
        { role: 'system', content: BASE_SYSTEM_PROMPT },
        ...messages.map((m) => ({ role: m.role as ApiMsg['role'], content: m.content })),
        { role: 'user', content: composedUserContent },
      ];
      let rounds = 0;
      let workMessages: ApiMsg[] = baseMessages;
      while (rounds < MAX_TOOL_ROUNDS) {
        rounds++;
        const result = await convexChat({
          messages: workMessages,
          tools: serializeTools(SKILL_TOOLS),
          model: DEFAULT_MODEL,
        });
        if (!result.ok) {
          setError(result.reason);
          return;
        }
        const content = applyToneConstraints(result.content).cleaned;
        if (result.toolCallsJson) {
          // Surface the assistant turn (the model may have included
          // narrative content alongside the tool_calls).
          setMessages((prev) => [
            ...prev,
            {
              id: makeId(),
              role: 'assistant',
              content,
              toolCallsJson: result.toolCallsJson ?? undefined,
            },
          ]);
          let toolCalls: ToolCallShape[];
          try {
            toolCalls = JSON.parse(result.toolCallsJson) as ToolCallShape[];
          } catch {
            setError('Malformed tool_calls payload from model');
            return;
          }
          // Append the assistant message with tool_calls so the model
          // can correlate tool_call_id on the next turn.
          workMessages = [
            ...workMessages,
            {
              role: 'assistant',
              content,
              toolCallsJson: result.toolCallsJson,
            },
          ];
          // Execute every tool call in this batch + append a
          // role:'tool' message per result.
          for (const call of toolCalls) {
            void logEvent({
              clientSlug: opts.clientSlug,
              event: 'tool-call',
              detail: `${call.function.name} ${call.function.arguments.slice(0, 120)}`,
            }).catch(() => undefined);
            const toolResult = await runToolCall(call, toolContext);
            void logEvent({
              clientSlug: opts.clientSlug,
              event: 'tool-result',
              detail: `${toolResult.name}`,
            }).catch(() => undefined);
            setMessages((prev) => [
              ...prev,
              {
                id: makeId(),
                role: 'tool',
                content: toolResult.resultJson,
                toolCallId: toolResult.toolCallId,
              },
            ]);
            workMessages.push({
              role: 'tool',
              content: toolResult.resultJson,
              toolCallId: toolResult.toolCallId,
            });
          }
          // Loop to re-invoke the model with the tool results.
          continue;
        }
        setMessages((prev) => [...prev, { id: makeId(), role: 'assistant', content }]);
        void logEvent({
          clientSlug: opts.clientSlug,
          event: 'stream',
          detail: `convex-backup complete (rounds=${rounds})`,
        }).catch(() => undefined);
        return;
      }
      setError(`Exceeded ${MAX_TOOL_ROUNDS} tool rounds`);
    },
    [messages, convexChat, opts.clientSlug, logEvent, toolContext],
  );

  return {
    messages,
    streamingText,
    route,
    isRunning,
    error,
    hasKey: openRouterKey.length > 0,
    maskedKey: maskKey(openRouterKey),
    send,
    cancel,
    reset,
  };
}
